"use client";

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { ExplanationResult, FollowUpMessage, SupportedLanguage } from "../types";
import { downloadMarkdownFile } from "@/lib/services/markdownExporter";
import ExplanationSkeleton from "./ExplanationSkeleton";
import CodeBlock from "./CodeBlock";

interface ExplanationOutputProps {
  explanation: ExplanationResult | null;
  isLoading: boolean;
  language: SupportedLanguage;
  errorMessage: string;
  provider?: string;
  followUpMessages?: FollowUpMessage[];
  onOpenFollowUp?: () => void;
}

const SEVERITY_STYLES = {
  critical: {
    badge: "bg-red-500/15 text-red-400 border-red-500/25",
    dot: "bg-red-400",
    label: "Critical",
  },
  warning: {
    badge: "bg-amber-500/15 text-amber-400 border-amber-500/25",
    dot: "bg-amber-400",
    label: "Warning",
  },
  info: {
    badge: "bg-blue-500/15 text-blue-400 border-blue-500/25",
    dot: "bg-blue-400",
    label: "Info",
  },
};

export default function ExplanationOutput({
  explanation,
  isLoading,
  language,
  errorMessage,
  provider,
  followUpMessages,
  onOpenFollowUp,
}: ExplanationOutputProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const copyAll = useCallback(async () => {
    if (!explanation) return;
    const text = [
      `ERROR TYPE: ${explanation.errorType}`,
      `\n📖 MEANING\n${explanation.meaning}`,
      `\n❓ WHY IT HAPPENS\n${explanation.whyItHappens}`,
      `\n🔧 HOW TO FIX IT\n${normalizedHowToFix.map((f, i) => `${i + 1}. ${f}`).join("\n")}`,
      `\n✅ EXAMPLE SOLUTION\n// Before:\n${normalizedExampleCode.before}\n\n// After:\n${normalizedExampleCode.after}`,
      `\n💡 ${normalizedExampleCode.explanation}`,
    ].join("\n");

    await navigator.clipboard.writeText(text);
    setCopiedSection("all");
    toast.success("Explanation copied to clipboard", { duration: 2500 });
    setTimeout(() => setCopiedSection(null), 2000);
  }, [explanation]);

  const handleDownload = useCallback(() => {
    if (!explanation) return;
    downloadMarkdownFile(errorMessage, language, explanation, provider, followUpMessages);
    toast.success("Markdown file downloaded", { duration: 2500 });
  }, [explanation, errorMessage, language, provider, followUpMessages]);

  if (isLoading) return <ExplanationSkeleton />;

  if (!explanation) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 flex flex-col items-center justify-center text-center gap-4 min-h-[320px]">
        <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-600">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div>
          <h3 className="text-zinc-300 font-semibold text-base mb-1">No explanation yet</h3>
          <p className="text-zinc-600 text-sm max-w-xs leading-relaxed">
            Paste an error message above, select your programming language, and click{" "}
            <span className="text-cyan-500 font-medium">"Explain This Error"</span> to get a structured breakdown.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          {["TypeError", "SyntaxError", "IndexError", "NullPointerException"].map((e) => (
            <span
              key={e}
              className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-500 border border-zinc-700"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {e}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const normalizedHowToFix = Array.isArray(explanation.howToFix)
    ? explanation.howToFix
    : explanation.howToFix
    ? [String(explanation.howToFix)]
    : [];

  const normalizedExampleCode =
    typeof explanation.exampleCode === 'string'
      ? {
          before: explanation.exampleCode,
          after: '',
          explanation: '',
        }
      : explanation.exampleCode;

  const severity = SEVERITY_STYLES[explanation.severity];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl animate-in fade-in duration-300">
      {/* Output Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${severity.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${severity.dot}`} />
              {severity.label}
            </span>
            <span
              className="text-sm font-semibold text-zinc-100"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {explanation.errorType}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-zinc-700 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-all duration-150"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download .md
          </button>
          <button
            onClick={copyAll}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 ${
              copiedSection === "all" ?"bg-emerald-500/15 text-emerald-400 border-emerald-500/25" :"bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-zinc-200"
            }`}
          >
            {copiedSection === "all" ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy All
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="divide-y divide-zinc-800">
        {/* Section 1: Meaning */}
        <ExplanationSection
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cyan-400">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          }
          label="Meaning"
          accent="cyan"
        >
          <p className="text-zinc-300 text-sm leading-7">{explanation.meaning}</p>
        </ExplanationSection>

        {/* Section 2: Why it happens */}
        <ExplanationSection
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-400">
              <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          }
          label="Why It Happens"
          accent="amber"
        >
          <p className="text-zinc-300 text-sm leading-7">{explanation.whyItHappens}</p>
        </ExplanationSection>

        {/* Section 3: How to fix */}
        <ExplanationSection
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
          }
          label="How to Fix It"
          accent="emerald"
        >
          <ol className="space-y-2.5">
            {normalizedHowToFix.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span
                  className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center mt-0.5"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {i + 1}
                </span>
                <span className="text-zinc-300 text-sm leading-6">{step}</span>
              </li>
            ))}
          </ol>
        </ExplanationSection>

        {/* Section 4: Example Solution */}
        <ExplanationSection
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-violet-400">
              <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
            </svg>
          }
          label="Example Solution"
          accent="violet"
        >
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-red-400 uppercase tracking-wide mb-1.5 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                ❌ Before (problematic code)
              </span>
              <CodeBlock code={normalizedExampleCode.before || ''} language={language} variant="error" />
            </div>
            <div>
              <span className="text-xs font-medium text-emerald-400 uppercase tracking-wide mb-1.5 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                ✅ After (fixed code)
              </span>
              <CodeBlock code={normalizedExampleCode.after || ''} language={language} variant="success" />
            </div>
            <div className="flex gap-2 bg-zinc-800/50 rounded-xl p-3 border border-zinc-700/50">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500 flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <p className="text-zinc-400 text-xs leading-5">{normalizedExampleCode.explanation}</p>
            </div>
          </div>
        </ExplanationSection>

        {/* Related Errors */}
        {explanation.relatedErrors && explanation.relatedErrors.length > 0 && (
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2.5">Related Errors</p>
            <div className="flex flex-wrap gap-2">
              {explanation.relatedErrors.map((err) => (
                <span
                  key={err}
                  className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600 hover:text-zinc-300 cursor-default transition-colors"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {err}
                </span>
              ))}
            </div>
          </div>
        )}

        {onOpenFollowUp && (
          <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-900/80">
            <button
              onClick={onOpenFollowUp}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:text-violet-400 hover:border-violet-500/25 hover:bg-violet-500/5 transition-all duration-150 text-sm"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Ask a follow-up
              {followUpMessages && followUpMessages.length > 0 && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/25">
                  {followUpMessages.filter((m) => m.role === 'user').length}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ExplanationSection({
  icon,
  label,
  accent,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  accent: "cyan" | "amber" | "emerald" | "violet";
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(true);

  const borderAccent = {
    cyan: "border-cyan-500/30",
    amber: "border-amber-500/30",
    emerald: "border-emerald-500/30",
    violet: "border-violet-500/30",
  }[accent];

  const labelColor = {
    cyan: "text-cyan-400",
    amber: "text-amber-400",
    emerald: "text-emerald-400",
    violet: "text-violet-400",
  }[accent];

  return (
    <div className={`border-l-2 ${borderAccent} ml-0`}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-zinc-800/30 transition-colors duration-150 text-left"
      >
        <div className="flex items-center gap-2.5">
          {icon}
          <span className={`text-sm font-semibold ${labelColor}`}>{label}</span>
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-zinc-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}